import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { catalogApi, CatalogContent, DesignTokens, TokenDefinition } from '../api';
import { useIsAdmin } from '../auth';
import {
  Layers,
  LayoutTemplate,
  Component,
  Link2,
  Palette,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  Clock,
  FileJson,
  Copy,
  Check,
  AlertTriangle,
  Loader2,
  Paintbrush,
  Type,
  Maximize2,
  Square,
  Sun,
  Ruler,
  Lock,
  Zap,
  Settings2,
} from 'lucide-react';
import clsx from 'clsx';

type TabId = 'overview' | 'themes' | 'templates' | 'sections' | 'bindings' | 'tokens';

export function CatalogPage() {
  const isAdmin = useIsAdmin();
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [copied, setCopied] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['catalog'],
    queryFn: () => catalogApi.getCurrent(),
  });

  const catalog = data?.data.data;
  const content = catalog?.catalog;

  const toggleExpand = (key: string) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleCopy = async () => {
    if (!content) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(content, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error || !catalog) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 mx-auto text-amber-400 mb-4" />
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Catalogo non trovato</h2>
        <p className="text-gray-600">
          Nessun catalogo disponibile. Esegui il seed del database per creare il catalogo iniziale.
        </p>
      </div>
    );
  }

  // Supporta sia array che oggetto per temi
  const themesRaw = (content as any)?.themes;
  const themesArray: Array<{ id: string; label: string; description?: string; preview?: { primary: string; secondary: string }; tokens?: Record<string, string> }> = 
    Array.isArray(themesRaw) ? themesRaw : 
    themesRaw ? Object.entries(themesRaw).map(([id, t]: [string, any]) => ({ id, ...t })) : [];

  const designTokens: DesignTokens | undefined = (content as any)?.designTokens;
  const tokenCount = designTokens ? Object.keys(designTokens.definitions || {}).length : 0;
  
  const tabs = [
    { id: 'overview' as TabId, label: 'Panoramica', icon: Layers },
    { id: 'themes' as TabId, label: 'Temi', icon: Palette, count: themesArray.length },
    { id: 'tokens' as TabId, label: 'Design Tokens', icon: Paintbrush, count: tokenCount },
    { id: 'templates' as TabId, label: 'Templates', icon: LayoutTemplate, count: Object.keys(content?.templates || {}).length },
    { id: 'sections' as TabId, label: 'Sezioni', icon: Component, count: Object.keys(content?.sections || {}).length },
    { id: 'bindings' as TabId, label: 'Binding Types', icon: Link2, count: Object.keys(content?.bindingTypes || {}).length },
  ];

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Layers className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h1 className="text-2xl font-bold text-gray-900">Product Catalog</h1>
                <span className={clsx(
                  'text-xs px-2 py-0.5 rounded-full',
                  catalog.status === 'published' 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-amber-100 text-amber-700'
                )}>
                  {catalog.status === 'published' ? (
                    <><CheckCircle className="w-3 h-3 inline mr-1" />Pubblicato</>
                  ) : (
                    <><Clock className="w-3 h-3 inline mr-1" />Bozza</>
                  )}
                </span>
              </div>
              <p className="text-gray-600">
                Versione {catalog.version}
                {catalog.publishedAt && (
                  <span className="text-gray-400 ml-2">
                    • Pubblicato il {new Date(catalog.publishedAt).toLocaleDateString('it-IT')}
                  </span>
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={handleCopy}
              className="btn-secondary text-sm"
            >
              {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
              {copied ? 'Copiato' : 'Copia JSON'}
            </button>
            {isAdmin && (
              <Link to="/catalog/versions" className="btn-primary text-sm inline-flex items-center">
                <FileJson className="w-4 h-4 mr-1" />
                Gestisci versioni
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={clsx(
                'flex items-center py-3 px-1 border-b-2 text-sm font-medium transition-colors',
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              )}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
              {tab.count !== undefined && (
                <span className={clsx(
                  'ml-2 px-2 py-0.5 text-xs rounded-full',
                  activeTab === tab.id ? 'bg-primary-100 text-primary-600' : 'bg-gray-100 text-gray-600'
                )}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'overview' && content && (
        <OverviewTab catalog={content} themes={themesArray} />
      )}
      
      {activeTab === 'themes' && (
        <ThemesTab themes={themesArray} />
      )}

      {activeTab === 'tokens' && designTokens && (
        <DesignTokensTab designTokens={designTokens} themes={themesArray} />
      )}
      {activeTab === 'tokens' && !designTokens && (
        <div className="text-center py-12">
          <Paintbrush className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">Nessun design token definito</p>
          <p className="text-sm text-gray-400 mt-1">
            I design tokens saranno disponibili dalla versione 7 del catalogo
          </p>
        </div>
      )}
      
      {activeTab === 'templates' && content && (
        <TemplatesTab 
          templates={content.templates} 
          expandedItems={expandedItems}
          onToggle={toggleExpand}
        />
      )}
      
      {activeTab === 'sections' && content && (
        <SectionsTab 
          sections={content.sections}
          expandedItems={expandedItems}
          onToggle={toggleExpand}
        />
      )}
      
      {activeTab === 'bindings' && content && (
        <BindingsTab 
          bindingTypes={content.bindingTypes || {}}
          expandedItems={expandedItems}
          onToggle={toggleExpand}
        />
      )}
    </div>
  );
}

// Theme interface (nuovo formato array)
interface ThemeItem {
  id: string;
  label: string;
  description?: string;
  preview?: {
    primary: string;
    secondary: string;
  };
}

// Overview Tab
function OverviewTab({ catalog, themes }: { catalog: CatalogContent; themes: ThemeItem[] }) {
  const stats = [
    { label: 'Temi', value: themes.length, color: 'bg-pink-100 text-pink-700' },
    { label: 'Page Kinds', value: catalog.allowedPageKinds?.length || 0, color: 'bg-blue-100 text-blue-700' },
    { label: 'Templates', value: Object.keys(catalog.templates || {}).length, color: 'bg-green-100 text-green-700' },
    { label: 'Sezioni', value: Object.keys(catalog.sections || {}).length, color: 'bg-purple-100 text-purple-700' },
    { label: 'Binding Types', value: Object.keys(catalog.bindingTypes || {}).length, color: 'bg-amber-100 text-amber-700' },
  ];

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {stats.map((stat) => (
          <div key={stat.label} className="card p-4">
            <p className="text-sm text-gray-500">{stat.label}</p>
            <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Themes preview */}
      {themes.length > 0 && (
        <div className="card p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Temi disponibili</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {themes.map((theme) => (
              <div key={theme.id} className="p-3 border border-gray-200 rounded-lg">
                {theme.preview && (
                  <div className="flex items-center space-x-2 mb-2">
                    <div 
                      className="w-4 h-4 rounded-full" 
                      style={{ backgroundColor: theme.preview.primary }}
                    />
                    <div 
                      className="w-4 h-4 rounded-full" 
                      style={{ backgroundColor: theme.preview.secondary }}
                    />
                  </div>
                )}
                <p className="font-medium text-gray-900 text-sm">{theme.label}</p>
                <p className="text-xs text-gray-500 font-mono">{theme.id}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Page Kinds */}
      <div className="card p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Page Kinds supportati</h3>
        <div className="flex flex-wrap gap-2">
          {(catalog.allowedPageKinds || []).map((kind) => (
            <span
              key={kind}
              className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-md text-sm font-medium"
            >
              {kind}
            </span>
          ))}
        </div>
      </div>

      {/* Quick summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Templates</h3>
          <ul className="space-y-2">
            {Object.entries(catalog.templates).slice(0, 5).map(([id, template]) => {
              const t = template as any;
              return (
                <li key={id} className="flex items-center justify-between text-sm">
                  <div>
                    <span className="font-mono text-gray-700">{id}</span>
                    {t.label && <span className="text-gray-400 ml-2">({t.label})</span>}
                  </div>
                  <span className="text-gray-500">{t.slots?.length || 0} slots</span>
                </li>
              );
            })}
            {Object.keys(catalog.templates).length > 5 && (
              <li className="text-sm text-gray-400">
                +{Object.keys(catalog.templates).length - 5} altri...
              </li>
            )}
          </ul>
        </div>

        <div className="card p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Sezioni</h3>
          <ul className="space-y-2">
            {Object.entries(catalog.sections).slice(0, 5).map(([id, section]) => {
              const s = section as any;
              return (
                <li key={id} className="flex items-center justify-between text-sm">
                  <div>
                    <span className="font-mono text-gray-700">{id}</span>
                    {s.label && <span className="text-gray-400 ml-2">({s.label})</span>}
                  </div>
                  <span className="text-gray-500">{s.variants?.length || 0} varianti</span>
                </li>
              );
            })}
            {Object.keys(catalog.sections).length > 5 && (
              <li className="text-sm text-gray-400">
                +{Object.keys(catalog.sections).length - 5} altre...
              </li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}

// Themes Tab
function ThemesTab({ themes }: { themes: ThemeItem[] }) {
  if (themes.length === 0) {
    return (
      <div className="text-center py-12">
        <Palette className="w-12 h-12 mx-auto text-gray-300 mb-4" />
        <p className="text-gray-500">Nessun tema definito</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {themes.map((theme) => (
        <div key={theme.id} className="card p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="font-semibold text-gray-900">{theme.label}</h3>
              <p className="text-sm text-gray-500 font-mono">{theme.id}</p>
            </div>
            {theme.preview && (
              <div className="flex space-x-1">
                <div 
                  className="w-8 h-8 rounded-lg shadow-inner" 
                  style={{ backgroundColor: theme.preview.primary }}
                  title="Primary"
                />
                <div 
                  className="w-8 h-8 rounded-lg shadow-inner" 
                  style={{ backgroundColor: theme.preview.secondary }}
                  title="Secondary"
                />
              </div>
            )}
          </div>
          
          {theme.description && (
            <p className="text-sm text-gray-600 mb-4">{theme.description}</p>
          )}
          
          {theme.preview && (
            <>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Primary</span>
                  <code className="px-2 py-0.5 bg-gray-100 rounded text-xs">{theme.preview.primary}</code>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Secondary</span>
                  <code className="px-2 py-0.5 bg-gray-100 rounded text-xs">{theme.preview.secondary}</code>
                </div>
              </div>
              
              {/* Preview bar */}
              <div className="mt-4 h-2 rounded-full overflow-hidden flex">
                <div className="flex-1" style={{ backgroundColor: theme.preview.primary }} />
                <div className="flex-1" style={{ backgroundColor: theme.preview.secondary }} />
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

// Template extended interface
interface TemplateExt {
  slots: string[];
  pageKind?: string;
  label?: string;
  description?: string;
}

// Templates Tab
function TemplatesTab({ 
  templates, 
  expandedItems, 
  onToggle 
}: { 
  templates: Record<string, { slots: string[] }>;
  expandedItems: Set<string>;
  onToggle: (key: string) => void;
}) {
  return (
    <div className="space-y-4">
      {Object.entries(templates).map(([id, template]) => {
        const t = template as TemplateExt;
        const isExpanded = expandedItems.has(`template-${id}`);
        return (
          <div key={id} className="card">
            <button
              onClick={() => onToggle(`template-${id}`)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
            >
              <div className="flex-1">
                <div className="flex items-center space-x-3">
                  <LayoutTemplate className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="font-mono font-medium text-gray-900">{id}</span>
                  {t.pageKind && (
                    <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                      {t.pageKind}
                    </span>
                  )}
                  <span className="text-sm text-gray-500">
                    {t.slots?.length || 0} slots
                  </span>
                </div>
                {t.label && (
                  <p className="text-sm text-gray-500 mt-1 ml-8">{t.label}</p>
                )}
              </div>
              {isExpanded ? (
                <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
              ) : (
                <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
              )}
            </button>
            
            {isExpanded && (
              <div className="px-6 pb-4 border-t border-gray-100">
                {t.description && (
                  <p className="text-sm text-gray-600 mt-4 mb-4">{t.description}</p>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  {t.pageKind && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">Page Kind:</p>
                      <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-sm">
                        {t.pageKind}
                      </span>
                    </div>
                  )}
                  
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Slots disponibili:</p>
                    <div className="flex flex-wrap gap-2">
                      {t.slots?.length > 0 ? t.slots.map((slot) => (
                        <code
                          key={slot}
                          className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm"
                        >
                          {slot}
                        </code>
                      )) : (
                        <span className="text-sm text-gray-400">Nessuno slot</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Sections Tab  
// Helper per estrarre ID variante (supporta sia stringa che oggetto)
function getVariantId(variant: string | { id: string; label: string; status?: string }): string {
  return typeof variant === 'string' ? variant : variant.id;
}

function getVariantLabel(variant: string | { id: string; label: string; status?: string }): string {
  return typeof variant === 'string' ? variant : variant.label;
}

function getVariantStatus(variant: string | { id: string; label: string; status?: string }): string | undefined {
  return typeof variant === 'string' ? undefined : variant.status;
}

function SectionsTab({ 
  sections, 
  expandedItems, 
  onToggle 
}: { 
  sections: Record<string, { 
    allowedSlots: string[]; 
    variants?: (string | { id: string; label: string; status?: string })[];
    label?: string;
    description?: string;
  }>;
  expandedItems: Set<string>;
  onToggle: (key: string) => void;
}) {
  return (
    <div className="space-y-4">
      {Object.entries(sections).map(([id, section]) => {
        const isExpanded = expandedItems.has(`section-${id}`);
        return (
          <div key={id} className="card">
            <button
              onClick={() => onToggle(`section-${id}`)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex-1 text-left">
                <div className="flex items-center space-x-3">
                  <Component className="w-5 h-5 text-purple-600" />
                  <span className="font-mono font-medium text-gray-900">{id}</span>
                  <span className="text-sm text-gray-500">
                    {section.allowedSlots?.length || 0} slots
                    {section.variants && section.variants.length > 0 && (
                      <span className="ml-2">• {section.variants.length} varianti</span>
                    )}
                  </span>
                </div>
                {section.label && (
                  <p className="text-sm text-gray-600 mt-1 ml-8">{section.label}</p>
                )}
              </div>
              {isExpanded ? (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronRight className="w-5 h-5 text-gray-400" />
              )}
            </button>
            
            {isExpanded && (
              <div className="px-6 pb-4 border-t border-gray-100 space-y-4">
                {section.description && (
                  <p className="mt-4 text-sm text-gray-600">{section.description}</p>
                )}
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Slots ammessi:</p>
                  <div className="flex flex-wrap gap-2">
                    {section.allowedSlots?.map((slot) => (
                      <code
                        key={slot}
                        className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm"
                      >
                        {slot}
                      </code>
                    ))}
                  </div>
                </div>
                
                {section.variants && section.variants.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Varianti:</p>
                    <div className="flex flex-wrap gap-2">
                      {section.variants.map((variant) => {
                        const variantId = getVariantId(variant);
                        const variantLabel = getVariantLabel(variant);
                        const variantStatus = getVariantStatus(variant);
                        return (
                          <span
                            key={variantId}
                            className={clsx(
                              'px-2 py-1 rounded text-sm',
                              variantStatus === 'planned' 
                                ? 'bg-amber-50 text-amber-700 border border-amber-200'
                                : variantStatus === 'deprecated'
                                ? 'bg-gray-100 text-gray-500 line-through'
                                : 'bg-purple-50 text-purple-700'
                            )}
                          >
                            {variantLabel}
                            {variantStatus === 'planned' && (
                              <Clock className="w-3 h-3 inline ml-1" />
                            )}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Bindings Tab
// Binding Type interface
interface BindingType {
  description?: string;
  modes?: string[];
  maxItems?: { min?: number; max?: number; default?: number };
  allowedFilters?: string[];
  dtoPath?: string;
  fields?: Record<string, unknown>;
  usedBy?: string[];
}

function BindingsTab({ 
  bindingTypes, 
  expandedItems, 
  onToggle 
}: { 
  bindingTypes: Record<string, unknown>;
  expandedItems: Set<string>;
  onToggle: (key: string) => void;
}) {
  if (Object.keys(bindingTypes).length === 0) {
    return (
      <div className="text-center py-12">
        <Link2 className="w-12 h-12 mx-auto text-gray-300 mb-4" />
        <p className="text-gray-500">Nessun binding type definito</p>
        <p className="text-sm text-gray-400 mt-1">
          I binding types definiscono come i dati vengono collegati alle sezioni
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {Object.entries(bindingTypes).map(([id, bindingType]) => {
        const isExpanded = expandedItems.has(`binding-${id}`);
        const config = bindingType as BindingType;
        
        return (
          <div key={id} className="card">
            <button
              onClick={() => onToggle(`binding-${id}`)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
            >
              <div className="flex-1">
                <div className="flex items-center space-x-3">
                  <Link2 className="w-5 h-5 text-amber-600 flex-shrink-0" />
                  <span className="font-mono font-medium text-gray-900">{id}</span>
                  {config.modes && (
                    <div className="flex gap-1">
                      {config.modes.map((mode) => (
                        <span key={mode} className="text-xs px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">
                          {mode}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {config.description && (
                  <p className="text-sm text-gray-500 mt-1 ml-8">{config.description}</p>
                )}
              </div>
              {isExpanded ? (
                <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
              ) : (
                <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
              )}
            </button>
            
            {isExpanded && (
              <div className="px-6 pb-4 border-t border-gray-100">
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Modes */}
                  {config.modes && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">Modalità:</p>
                      <div className="flex flex-wrap gap-2">
                        {config.modes.map((mode) => (
                          <span key={mode} className="px-2 py-1 bg-amber-50 text-amber-700 rounded text-sm">
                            {mode}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Max Items */}
                  {config.maxItems && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">Limiti elementi:</p>
                      <p className="text-sm text-gray-600">
                        Min: {config.maxItems.min || 1}, 
                        Max: {config.maxItems.max || '∞'}, 
                        Default: {config.maxItems.default || '-'}
                      </p>
                    </div>
                  )}
                  
                  {/* Allowed Filters */}
                  {config.allowedFilters && config.allowedFilters.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">Filtri ammessi:</p>
                      <div className="flex flex-wrap gap-2">
                        {config.allowedFilters.map((filter) => (
                          <code key={filter} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm">
                            {filter}
                          </code>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* DTO Path */}
                  {config.dtoPath && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">DTO Path:</p>
                      <code className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-sm">
                        {config.dtoPath}
                      </code>
                    </div>
                  )}
                  
                  {/* Used By */}
                  {config.usedBy && config.usedBy.length > 0 && (
                    <div className="md:col-span-2">
                      <p className="text-sm font-medium text-gray-700 mb-2">Usato da:</p>
                      <div className="flex flex-wrap gap-2">
                        {config.usedBy.map((section) => (
                          <span key={section} className="px-2 py-1 bg-purple-50 text-purple-700 rounded text-sm font-mono">
                            {section}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Fields */}
                {config.fields && Object.keys(config.fields).length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Campi:</p>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                      {Object.entries(config.fields).map(([fieldName, fieldConfig]) => {
                        const field = fieldConfig as Record<string, unknown>;
                        return (
                          <div key={fieldName} className="flex items-start justify-between text-sm">
                            <div className="flex items-center space-x-2">
                              <code className="font-mono text-gray-800">{fieldName}</code>
                              {!!field.required && (
                                <span className="text-red-500 text-xs">*</span>
                              )}
                            </div>
                            <div className="text-right text-gray-500">
                              <span className="px-1.5 py-0.5 bg-gray-200 rounded text-xs">
                                {String(field.type || 'unknown')}
                              </span>
                              {field.default !== undefined && (
                                <span className="ml-2 text-xs">
                                  default: {String(field.default)}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ============================================================
// Design Tokens Tab
// ============================================================

const CATEGORY_META: Record<string, { label: string; icon: typeof Paintbrush; color: string }> = {
  colors:     { label: 'Colori',      icon: Paintbrush,  color: 'text-pink-600' },
  typography: { label: 'Tipografia',   icon: Type,        color: 'text-blue-600' },
  spacing:    { label: 'Spaziature',   icon: Maximize2,   color: 'text-green-600' },
  borders:    { label: 'Bordi',        icon: Square,      color: 'text-orange-600' },
  shadows:    { label: 'Ombre',        icon: Sun,         color: 'text-purple-600' },
  layout:     { label: 'Layout',       icon: Ruler,       color: 'text-teal-600' },
};

const EDITABLE_LABELS: Record<string, { label: string; icon: typeof Lock; color: string }> = {
  client:   { label: 'Editabile',   icon: Paintbrush, color: 'bg-green-100 text-green-700' },
  auto:     { label: 'Automatico',  icon: Zap,        color: 'bg-blue-100 text-blue-700' },
  advanced: { label: 'Avanzato',    icon: Settings2,  color: 'bg-amber-100 text-amber-700' },
  fixed:    { label: 'Fisso',       icon: Lock,        color: 'bg-gray-100 text-gray-500' },
};

function DesignTokensTab({ 
  designTokens,
  themes,
}: { 
  designTokens: DesignTokens;
  themes: Array<{ id: string; label: string; tokens?: Record<string, string> }>;
}) {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showFixed, setShowFixed] = useState(false);
  const [compareTheme, setCompareTheme] = useState<string>('');

  const { definitions, defaults, fontPairings } = designTokens;

  // Raggruppa per categoria
  const byCategory: Record<string, Array<[string, TokenDefinition]>> = {};
  for (const [key, def] of Object.entries(definitions)) {
    const cat = def.category;
    if (!byCategory[cat]) byCategory[cat] = [];
    byCategory[cat].push([key, def]);
  }

  // Conteggi per editabilità
  const editCounts = { client: 0, auto: 0, advanced: 0, fixed: 0 };
  for (const def of Object.values(definitions)) {
    editCounts[def.editable as keyof typeof editCounts]++;
  }

  // Token del tema selezionato per confronto
  const themeTokens = compareTheme 
    ? themes.find(t => t.id === compareTheme)?.tokens || {}
    : {};

  // Filtra token da mostrare
  const filteredEntries = Object.entries(definitions).filter(([, def]) => {
    if (selectedCategory !== 'all' && def.category !== selectedCategory) return false;
    if (!showFixed && def.editable === 'fixed') return false;
    return true;
  });

  // Raggruppa filtered per categoria
  const filteredByCategory: Record<string, Array<[string, TokenDefinition]>> = {};
  for (const [key, def] of filteredEntries) {
    const cat = def.category;
    if (!filteredByCategory[cat]) filteredByCategory[cat] = [];
    filteredByCategory[cat].push([key, def]);
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Object.entries(editCounts).map(([level, count]) => {
          const meta = EDITABLE_LABELS[level];
          return (
            <div key={level} className="card p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">{meta.label}</p>
                  <p className="text-2xl font-bold text-gray-900">{count}</p>
                </div>
                <span className={clsx('p-2 rounded-lg', meta.color)}>
                  <meta.icon className="w-4 h-4" />
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Filtri */}
      <div className="card p-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* Categoria */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Categoria:</span>
            <div className="flex flex-wrap gap-1">
              <button
                onClick={() => setSelectedCategory('all')}
                className={clsx(
                  'px-2.5 py-1 text-xs rounded-full transition-colors',
                  selectedCategory === 'all'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                )}
              >
                Tutti ({Object.keys(definitions).length})
              </button>
              {Object.entries(CATEGORY_META).map(([cat, meta]) => {
                const count = byCategory[cat]?.length || 0;
                if (count === 0) return null;
                return (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={clsx(
                      'px-2.5 py-1 text-xs rounded-full transition-colors',
                      selectedCategory === cat
                        ? 'bg-gray-900 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    )}
                  >
                    {meta.label} ({count})
                  </button>
                );
              })}
            </div>
          </div>

          {/* Mostra fissi */}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={showFixed}
              onChange={(e) => setShowFixed(e.target.checked)}
              className="rounded border-gray-300"
            />
            <span className="text-gray-600">Mostra token fissi</span>
          </label>

          {/* Confronta tema */}
          {themes.some(t => t.tokens && Object.keys(t.tokens).length > 0) && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Confronta con:</span>
              <select
                value={compareTheme}
                onChange={(e) => setCompareTheme(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-2 py-1"
              >
                <option value="">Default</option>
                {themes.filter(t => t.tokens).map(t => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Token per categoria */}
      {Object.entries(filteredByCategory).map(([cat, entries]) => {
        const meta = CATEGORY_META[cat] || { label: cat, icon: Layers, color: 'text-gray-600' };
        const CatIcon = meta.icon;

        return (
          <div key={cat} className="space-y-3">
            <div className="flex items-center gap-2">
              <CatIcon className={clsx('w-5 h-5', meta.color)} />
              <h3 className="font-semibold text-gray-900">{meta.label}</h3>
              <span className="text-xs text-gray-400">({entries.length})</span>
            </div>

            <div className="card overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Token</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Label</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Default</th>
                    {compareTheme && (
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        {themes.find(t => t.id === compareTheme)?.label}
                      </th>
                    )}
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Editabilità</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {entries.map(([key, def]) => {
                    const defaultVal = defaults[key] || '';
                    const themeVal = themeTokens[key];
                    const editMeta = EDITABLE_LABELS[def.editable] || EDITABLE_LABELS.fixed;
                    const isColor = def.type === 'color';
                    const isDiff = themeVal !== undefined && themeVal !== defaultVal;

                    return (
                      <tr key={key} className="hover:bg-gray-50">
                        <td className="px-4 py-2">
                          <code className="text-sm font-mono text-gray-800">{key}</code>
                          {def.derivedFrom && (
                            <div className="text-xs text-gray-400 mt-0.5">
                              ← {def.derivedFrom}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          <span className="text-sm text-gray-700">{def.label}</span>
                          {def.description && (
                            <p className="text-xs text-gray-400 mt-0.5 max-w-xs truncate" title={def.description}>
                              {def.description}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          <TokenValueCell value={defaultVal} isColor={isColor} />
                        </td>
                        {compareTheme && (
                          <td className="px-4 py-2">
                            {themeVal ? (
                              <TokenValueCell value={themeVal} isColor={isColor} highlight={isDiff} />
                            ) : (
                              <span className="text-xs text-gray-300">—</span>
                            )}
                          </td>
                        )}
                        <td className="px-4 py-2">
                          <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">
                            {def.type}
                          </span>
                        </td>
                        <td className="px-4 py-2">
                          <span className={clsx('text-xs px-1.5 py-0.5 rounded inline-flex items-center gap-1', editMeta.color)}>
                            <editMeta.icon className="w-3 h-3" />
                            {editMeta.label}
                          </span>
                          {def.generates && (
                            <span className="text-xs text-gray-400 ml-1" title={`Genera: ${def.generates.join(', ')}`}>
                              →{def.generates.length}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {filteredEntries.length === 0 && (
        <div className="text-center py-12">
          <Paintbrush className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">Nessun token corrisponde ai filtri</p>
        </div>
      )}

      {/* Font Pairings */}
      {fontPairings && fontPairings.length > 0 && (selectedCategory === 'all' || selectedCategory === 'typography') && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Type className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold text-gray-900">Combinazioni font consigliate</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {fontPairings.map((pair) => (
              <div key={pair.id} className="card p-4">
                <p className="text-xs text-gray-400 font-mono mb-2">{pair.id}</p>
                <p className="text-sm font-medium text-gray-900 mb-1">{pair.label}</p>
                <div className="space-y-1 text-sm">
                  <p className="text-gray-600">
                    <span className="text-gray-400">Heading:</span> {pair.heading}
                  </p>
                  <p className="text-gray-600">
                    <span className="text-gray-400">Body:</span> {pair.body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Preview inline di un valore token nella tabella
function TokenValueCell({ value, isColor, highlight }: { 
  value: string; 
  isColor?: boolean;
  highlight?: boolean;
}) {
  if (!value) return <span className="text-xs text-gray-300">—</span>;

  const looksLikeColor = isColor || /^(#|rgb|hsl)/.test(value);

  return (
    <div className={clsx('flex items-center gap-2', highlight && 'font-medium')}>
      {looksLikeColor && (
        <div
          className="w-5 h-5 rounded border border-gray-200 flex-shrink-0"
          style={{ backgroundColor: value }}
          title={value}
        />
      )}
      <code className={clsx(
        'text-xs font-mono truncate max-w-[160px]',
        highlight ? 'text-blue-700' : 'text-gray-600'
      )} title={value}>
        {value}
      </code>
    </div>
  );
}
